/**
 * The open seam (1.I-1 deliverable 12): every row this package renders is an
 * `<a href={docHref(id)}>` that calls the handler from `OpenDocContext`. The default navigates
 * to the page for a plain primary click or Enter and leaves a modified or middle click to the
 * browser; 1.I-2 supplies a handler that adds the open modes.
 */
import { useNavigate } from '@tanstack/react-router';
import { createContext, createElement, type ReactNode, useCallback, useContext } from 'react';

/** The parts of a click or key event the seam reads (React's and the DOM's both fit). */
export interface OpenEvent {
  readonly button?: number | undefined;
  readonly key?: string | undefined;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  preventDefault(): void;
}

export type OpenDocHandler = (docId: string, event?: OpenEvent) => void;

export const OpenDocContext = createContext<OpenDocHandler | null>(null);

/** A plain primary click, or Enter, with no modifier. */
export function isPlainActivation(event: OpenEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (event.key !== undefined) return event.key === 'Enter';
  return (event.button ?? 0) === 0;
}

/** A plain in-app navigation for a same-origin path (a page load is the browser's, for the rest). */
export function useGoTo(): (path: string, event?: OpenEvent) => void {
  const navigate = useNavigate();
  return useCallback(
    (path, event) => {
      if (event) {
        if (!isPlainActivation(event)) return;
        event.preventDefault();
      }
      void navigate({ to: path as '/' });
    },
    [navigate],
  );
}

/** The built-in handler: a plain primary click or Enter navigates in place. */
export function useDefaultOpenDoc(): OpenDocHandler {
  const navigate = useNavigate();
  return useCallback<OpenDocHandler>(
    (docId, event) => {
      if (event) {
        if (!isPlainActivation(event)) return;
        event.preventDefault();
      }
      void navigate({ to: '/doc/$id', params: { id: docId } });
    },
    [navigate],
  );
}

/** Provides the default handler; `AppRoot` renders it, and a wrapper (1.I-2) nests its own inside. */
export function DefaultOpenDocProvider({ children }: { children: ReactNode }) {
  return createElement(OpenDocContext.Provider, { value: useDefaultOpenDoc() }, children);
}

export function useOpenDoc(): OpenDocHandler {
  const provided = useContext(OpenDocContext);
  const fallback = useDefaultOpenDoc();
  return provided ?? fallback;
}
